import { useEffect, useState } from 'react';

/**
 * Which of `ids` is currently the section you are looking at.
 *
 * For a panel whose rows are anchors into one long screen rather than
 * separate routes. Without this, two rows that both point at /settings both
 * match the URL and both render as selected, which tells you nothing.
 *
 * `rootMargin` biases the observer toward the top of the viewport so a
 * section counts as current once its heading is near the top, not only when
 * it fills the screen.
 */
export function useScrollSpy(ids: string[], offset = 96): string | null {
  const [active, setActive] = useState<string | null>(ids[0] ?? null);

  useEffect(() => {
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length) setActive(visible[0].target.id);
      },
      { rootMargin: `-${offset}px 0px -55% 0px`, threshold: 0 },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids, offset]);

  return active;
}

/** Scroll a section into view under the topbar. */
export function scrollToSection(id: string): void {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
